import React, { useEffect, useRef } from "react"
import PropTypes from "prop-types"
import { graphql, useStaticQuery } from "gatsby"
import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Typography,
} from "@mui/material"
import twemoji from "@twemoji/api"

const DEFAULT_CTA_LABEL = "Amazonで見る"
const DEFAULT_TITLE = "Amazonの商品を見る"
const DEFAULT_IMAGE = {
  bgcolor: "#ffecb3",
  icon: "🛒",
}
const AMAZON_MARKETPLACE_HOSTS = new Set(["amazon.co.jp", "www.amazon.co.jp"])

const isAmazonMarketplaceUrl = value => {
  try {
    const parsedUrl = new URL(value)
    return (
      parsedUrl.protocol === "https:" &&
      AMAZON_MARKETPLACE_HOSTS.has(parsedUrl.hostname.toLowerCase())
    )
  } catch (error) {
    return false
  }
}

export const buildAmazonUrl = (url, associateTag) => {
  if (!associateTag || !isAmazonMarketplaceUrl(url)) {
    return url
  }

  const parsedUrl = new URL(url)
  parsedUrl.searchParams.set("tag", associateTag)
  return parsedUrl.toString()
}

const trackAmazonLinkEvent = eventName => {
  try {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("event", eventName, {
        affiliate_provider: "amazon",
        link_placement: "article",
      })
    }
  } catch (error) {
    // 計測の失敗でリンク操作を妨げない
  }
}

export const trackAmazonLinkClick = () => {
  trackAmazonLinkEvent("affiliate_link_click")
}

export const trackAmazonLinkImpression = () => {
  trackAmazonLinkEvent("affiliate_link_impression")
}

const AmazonLink = ({
  url,
  title: titleOverride,
  image: imageOverride,
  ctaLabel = DEFAULT_CTA_LABEL,
  associateTag = process.env.GATSBY_AMAZON_ASSOCIATE_TAG,
}) => {
  const cardRef = useRef(null)
  const data = useStaticQuery(graphql`
    query AmazonLinksQuery {
      allDataJson(filter: { AmazonLinks: {} }) {
        edges {
          node {
            AmazonLinks {
              sourceUrl
              resolvedUrl
              asin
              title
              imageUrl
              detailPageUrl
              source
              fetchedAt
            }
          }
        }
      }
    }
  `)
  const cachedLink = data.allDataJson.edges
    .flatMap(edge => edge.node.AmazonLinks || [])
    .find(link => link.sourceUrl === url)
  const title = titleOverride || cachedLink?.title || DEFAULT_TITLE
  const image = imageOverride || cachedLink?.imageUrl
  const cachedUrl = cachedLink?.detailPageUrl || cachedLink?.resolvedUrl
  const href = cachedUrl
    ? buildAmazonUrl(cachedUrl, associateTag)
    : buildAmazonUrl(url, associateTag)
  const linkAriaLabel = `${title}をAmazonの商品ページで開く`

  useEffect(() => {
    const card = cardRef.current
    if (!card || typeof window.IntersectionObserver !== "function") {
      return undefined
    }
    const observer = new window.IntersectionObserver(
      entries => {
        if (!entries.some(entry => entry.isIntersecting)) return

        trackAmazonLinkImpression()
        observer.unobserve(card)
      },
      { threshold: 0.5 }
    )
    observer.observe(card)

    return () => observer.disconnect()
  }, [])

  if (!url) {
    return null
  }

  return (
    <Card
      ref={cardRef}
      component="aside"
      aria-label="Amazon商品リンク"
      sx={{
        display: "flex",
        maxWidth: 700,
        my: 2,
        marginLeft: 4,
      }}
    >
      <CardActionArea
        component="a"
        href={href}
        target="_blank"
        rel="sponsored noopener noreferrer"
        aria-label={linkAriaLabel}
        onClick={trackAmazonLinkClick}
        sx={{
          display: "flex",
          justifyContent: "start",
          alignItems: "stretch",
          flexGrow: 1,
        }}
      >
        <CardContent
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            p: 2,
          }}
        >
          <Typography variant="subtitle2" component="h3">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {ctaLabel}（PR・アフィリエイトリンク）
          </Typography>
        </CardContent>
        {image ? (
          <CardMedia
            component="img"
            image={image}
            alt={title}
            sx={{
              width: 151,
              flexShrink: 0,
              objectFit: "contain",
              p: 1,
            }}
          />
        ) : (
          <Typography
            role="img"
            aria-label="商品画像なし"
            component="div"
            variant="h2"
            sx={{
              width: 151,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              bgcolor: DEFAULT_IMAGE.bgcolor,
              "& img": {
                width: 48,
                height: 48,
              },
            }}
            dangerouslySetInnerHTML={{
              __html: twemoji.parse(DEFAULT_IMAGE.icon),
            }}
          />
        )}
      </CardActionArea>
    </Card>
  )
}

AmazonLink.propTypes = {
  url: PropTypes.string.isRequired,
  title: PropTypes.string,
  image: PropTypes.string,
  ctaLabel: PropTypes.string,
  associateTag: PropTypes.string,
}

export default AmazonLink
